import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  getAppSettings,
  deleteAppSetting,
} from '../../redux/slices/appSettingSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import alertify from 'alertifyjs';

/**
 * AppSettingListPage - Admin app settings page
 *
 * Features:
 * - App setting table with search
 * - Create, Edit, Delete actions
 * - Value truncation for long values
 * - Pagination
 *
 * Permission: admin.app-settings.view
 */
const AppSettingListPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { settings, loading, error, pagination } = useSelector(
    (state) => state.appSettings
  );

  const hasViewPermission = useHasPermission('admin.app-settings.view');
  const hasCreatePermission = useHasPermission('admin.app-settings.create');
  const hasEditPermission = useHasPermission('admin.app-settings.edit');
  const hasDeletePermission = useHasPermission('admin.app-settings.delete');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);

  // Debounce search term (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch app settings
  useEffect(() => {
    if (hasViewPermission) {
      const params = {
        search: debouncedSearch || undefined,
        page: page + 1,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      dispatch(getAppSettings(params));
    }
  }, [dispatch, debouncedSearch, hasViewPermission, page, limit]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateNew = () => {
    history.push('/admin/app-settings/new');
  };

  const handleEdit = (id) => {
    history.push(`/admin/app-settings/${id}/edit`);
  };

  const handleDeleteClick = (setting) => {
    setSettingToDelete(setting);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!settingToDelete) return;

    try {
      await dispatch(deleteAppSetting(settingToDelete._id)).unwrap();
      alertify.success('Sistem ayarı başarıyla silindi');
      setDeleteDialogOpen(false);
      setSettingToDelete(null);
      // Refresh list
      dispatch(getAppSettings({
        search: debouncedSearch || undefined,
        page: page + 1,
        limit,
      }));
    } catch (err) {
      alertify.error(err.message || 'Sistem ayarı silinemedi');
    }
  };

  // Truncate long values for display
  const truncateValue = (value, maxLength = 50) => {
    if (!value) return '-';
    if (value.length <= maxLength) return value;
    return `${value.substring(0, maxLength)}...`;
  };

  if (!hasViewPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
            <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Sistem Ayarları
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uygulama genelinde key-value ayarlarını yönetin
              </Typography>
            </Box>
          </Box>
          {hasCreatePermission && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Yeni Ayar Oluştur
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Anahtar veya açıklama ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 250 }}
            />
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Anahtar</TableCell>
                <TableCell>Açıklama</TableCell>
                <TableCell>Değer</TableCell>
                <TableCell>Oluşturulma Tarihi</TableCell>
                <TableCell>Güncellenme Tarihi</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : settings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Sistem ayarı bulunamadı
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                settings.map((setting) => (
                  <TableRow key={setting._id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {setting.key}
                      </Typography>
                    </TableCell>
                    <TableCell>{setting.description}</TableCell>
                    <TableCell>
                      <Tooltip title={setting.value || '-'} arrow>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {truncateValue(setting.value, 50)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {setting.createdAt
                        ? format(new Date(setting.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {setting.updatedAt
                        ? format(new Date(setting.updatedAt), 'dd MMM yyyy HH:mm', { locale: tr })
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {hasEditPermission && (
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(setting._id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasDeletePermission && (
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(setting)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleLimitChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count}`
          }
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Sistem Ayarını Sil</DialogTitle>
          <DialogContent>
            <Typography>
              "{settingToDelete?.key}" anahtarlı sistem ayarını silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Sil
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AppSettingListPage;

